import {
  Component,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  viewChild,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuroraLight } from '../shared/aurora-light/aurora-light';
import { SphereAnimation } from '../home-page/sphere-animation/sphere-animation';
import { GsValueCard } from './gs-value-card/gs-value-card';
import { GsRoadmapCard } from './gs-roadmap-card/gs-roadmap-card';
import { WN_TABS, WN_TAB_CONTENT, WN_ROADMAP_ITEMS, DATANEX_TRAILER_URL } from '../app.constants';
import { ContributeAssetButton } from '../shared/contribute-asset';

@Component({
  selector: 'get-started-page',
  imports: [AuroraLight, SphereAnimation, GsValueCard, GsRoadmapCard, ContributeAssetButton],
  templateUrl: './get-started-page.html',
  styleUrl: './get-started-page.scss',
})
export class GetStartedPage implements AfterViewInit, OnDestroy {
  private router = inject(Router);
  private el = inject(ElementRef<HTMLElement>);
  private readonly cleanupFns: Array<() => void> = [];

  heroVideoRef = viewChild<ElementRef<HTMLVideoElement>>('heroVideo');

  readonly tabs = signal(WN_TABS);
  readonly tabContent = signal(WN_TAB_CONTENT);
  readonly roadmapItems = signal(WN_ROADMAP_ITEMS);
  readonly trailerUrl = DATANEX_TRAILER_URL;

  ngAfterViewInit(): void {
    const video = this.heroVideoRef()?.nativeElement;
    if (video) {
      video.muted = true;
      video.play().catch(() => {});
    }

    const host = this.el.nativeElement as HTMLElement;

    // Sync --nav-w so the fixed sphere centers in the content area.
    const navEl = document.querySelector('mat-sidenav') as HTMLElement | null;
    const syncNavW = () => host.style.setProperty('--nav-w', (navEl?.offsetWidth ?? 260) + 'px');
    syncNavW();
    if (navEl) {
      const navRO = new ResizeObserver(syncNavW);
      navRO.observe(navEl);
      this.cleanupFns.push(() => navRO.disconnect());
    }

    let counterStarted = false;

    // root: null = browser viewport — works regardless of which ancestor
    // element handles scrolling (mat-sidenav-content, .wn, or window).
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target as HTMLElement;
          el.classList.add('wn--visible');
          observer.unobserve(el);
          if (!counterStarted && el.classList.contains('wn__assets')) {
            counterStarted = true;
            this.animateCounters(host);
          }
        });
      },
      { root: null, rootMargin: '0px 0px -5% 0px', threshold: 0.05 },
    );

    this.cleanupFns.push(() => observer.disconnect());
    host.querySelectorAll<HTMLElement>('.wn__reveal').forEach((el) => observer.observe(el));

    this.initSphereSharpObserver(host);
  }

  ngOnDestroy(): void {
    this.cleanupFns.forEach((fn) => fn());
  }

  private animateCounters(host: HTMLElement): void {
    host.querySelectorAll<HTMLElement>('.wn__assets-count').forEach((el) => {
      const target = parseInt(el.dataset['target'] ?? '0', 10);
      const suffix = el.dataset['suffix'] ?? '';
      const duration = 2000;
      let start = -1;
      const tick = (now: number): void => {
        // Initialise start from the first real rAF timestamp so there is
        // no gap between when the counter is scheduled and when it begins.
        if (start < 0) start = now;
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = Math.round(eased * target) + suffix;
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }

  private initSphereSharpObserver(host: HTMLElement): void {
    const heroEl = host.querySelector('.wn__hero') as HTMLElement | null;
    // sphere-animation renders its canvas as .home__sphere
    const sphereEl = host.querySelector('.home__sphere') as HTMLCanvasElement | null;
    if (!heroEl || !sphereEl) return;

    // Sharpens when hero has scrolled mostly out of view
    const heroObs = new IntersectionObserver(
      ([entry]) => sphereEl.classList.toggle('wn__sphere--sharp', entry.intersectionRatio < 0.72),
      { threshold: [0, 0.1, 0.4, 0.72, 1] },
    );
    heroObs.observe(heroEl);
    this.cleanupFns.push(() => heroObs.disconnect());

    // Fades further when the assets section is visible
    const assetsEl = host.querySelector('.wn__assets') as HTMLElement | null;
    if (assetsEl) {
      const assetsObs = new IntersectionObserver(
        ([entry]) => sphereEl.classList.toggle('wn__sphere--faded', entry.isIntersecting),
        { threshold: 0.1 },
      );
      assetsObs.observe(assetsEl);
      this.cleanupFns.push(() => assetsObs.disconnect());
    }
  }

  navigateToDiscover(): void {
    this.router.navigate(['/home']);
  }

  navigateToAssets(): void {
    this.router.navigate(['/catalog']);
  }
}
